/**
 * US mobile phone helpers for landing + field forms.
 * Enforces NANP 10-digit numbers (optional leading country code 1).
 */

/** Max characters when typing formatted US phone: (###) ###-#### */
export const US_PHONE_INPUT_MAX_LENGTH = 14;

export function phoneDigitsOnly(raw?: string | null): string {
  return String(raw || '').replace(/\D/g, '');
}

/**
 * Normalize to 10-digit US national number, or null if invalid.
 * Accepts 10 digits, or 11 starting with 1.
 */
export function normalizeUsPhoneDigits(raw?: string | null): string | null {
  let digits = phoneDigitsOnly(raw);
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  if (digits.length !== 10) return null;
  // NANP: area code and exchange cannot start with 0 or 1
  if (digits[0] === '0' || digits[0] === '1') return null;
  if (digits[3] === '0' || digits[3] === '1') return null;
  return digits;
}

export function isValidUsPhone(raw?: string | null): boolean {
  return normalizeUsPhoneDigits(raw) !== null;
}

/**
 * Format for display while typing. Strips non-digits, drops leading 1 when
 * 11 digits, caps at 10 national digits, then applies (###) ###-####.
 */
export function formatUsPhoneInput(raw: string): string {
  let digits = phoneDigitsOnly(raw);
  if (digits.length > 10 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  digits = digits.slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** E.164 US form for SMS APIs: +1XXXXXXXXXX */
export function toUsE164(raw?: string | null): string | null {
  const digits = normalizeUsPhoneDigits(raw);
  return digits ? `+1${digits}` : null;
}

export function usPhoneValidationMessage(raw?: string | null, emptyOk = false): string | null {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return emptyOk ? null : 'Enter a valid US mobile phone number.';
  if (isValidUsPhone(trimmed)) return null;
  return 'Enter a valid 10-digit US phone number (example: (575) 555-0100).';
}
